import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DarkModeRounded as DarkModeRoundedIcon,
  LightModeRounded as LightModeRoundedIcon,
  CurrencyExchangeRounded as CurrencyExchangeRoundedIcon,
  GetAppRounded as GetAppRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  LanguageRounded as LanguageRoundedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { isNativeApp, triggerHaptic } from '../utils/nativeBridge';
import AboutModal from './AboutModal';

const Header = ({ toggleTheme, language = 'fa', toggleLanguage, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    // Check if app is running natively or as standalone PWA
    if (isNativeApp() || window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          setIsInstalled(true);
        }
        setDeferredPrompt(null);
      });
    } else {
      setInfoOpen(true);
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: isDark ? 'rgba(11, 15, 23, 0.85)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
        color: 'text.primary',
        top: 0,
        zIndex: theme.zIndex.appBar,
        pt: 'max(0px, env(safe-area-inset-top))',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 } }}>
          {/* Logo & Brand Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
              }}
            >
              <CurrencyExchangeRoundedIcon />
            </Box>

            <Box>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.2rem', sm: '1.35rem' },
                  letterSpacing: '-0.02em',
                  background: isDark
                    ? 'linear-gradient(135deg, #FFFFFF 0%, #94A3B8 100%)'
                    : 'linear-gradient(135deg, #0F172A 0%, #334155 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {t('appTitle')}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons: PWA Install + Language Switcher + Theme Switcher */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isInstalled && (
              isMobile ? (
                <Tooltip title={t('installTooltip')}>
                  <IconButton
                    onClick={handleInstallClick}
                    color="primary"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: isDark ? 'rgba(59, 130, 246, 0.4)' : 'rgba(37, 99, 235, 0.3)',
                      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#EFF6FF',
                      color: theme.palette.primary.main,
                    }}
                  >
                    <GetAppRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<GetAppRoundedIcon />}
                  onClick={handleInstallClick}
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: '0.825rem',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  }}
                >
                  {t('installApp')}
                </Button>
              )
            )}

            {/* Language Switcher Button */}
            <Tooltip title={t('langTooltip')}>
              {isMobile ? (
                <Button
                  onClick={() => {
                    triggerHaptic('light');
                    toggleLanguage();
                  }}
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<LanguageRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    height: 40,
                    minWidth: 64,
                    px: 1.25,
                    borderRadius: 2.5,
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                  }}
                >
                  {language === 'fa' ? 'EN' : 'FA'}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    triggerHaptic('light');
                    toggleLanguage();
                  }}
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<LanguageRoundedIcon />}
                  sx={{
                    height: 40,
                    px: 2,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: '0.825rem',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
                  }}
                >
                  {t('langToggle')}
                </Button>
              )}
            </Tooltip>

            {/* Theme Switcher Button */}
            <Tooltip title={isDark ? t('themeLight') : t('themeDark')}>
              <IconButton
                onClick={() => {
                  triggerHaptic('light');
                  toggleTheme();
                }}
                color="inherit"
                aria-label="Toggle theme"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  backgroundColor: isDark ? 'rgba(20, 28, 44, 0.6)' : '#F1F5F9',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : '#E2E8F0',
                  },
                }}
              >
                {isDark ? (
                  <LightModeRoundedIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
                ) : (
                  <DarkModeRoundedIcon sx={{ color: '#475569', fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>

            {/* About / Info Button */}
            <Tooltip title={t('about')}>
              <IconButton
                onClick={() => {
                  triggerHaptic('light');
                  setAboutOpen(true);
                }}
                color="inherit"
                aria-label="About ArzGo"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  backgroundColor: isDark ? 'rgba(20, 28, 44, 0.6)' : '#F1F5F9',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : '#E2E8F0',
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ color: isDark ? '#93C5FD' : '#2563EB', fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* PWA Manual Install Instructions Dialog */}
          <Dialog
            open={infoOpen}
            onClose={() => setInfoOpen(false)}
            maxWidth="xs"
            fullWidth
            sx={{
              '& .MuiDialog-container': {
                pt: { xs: 'calc(env(safe-area-inset-top, 24px) + 12px)', sm: 2 },
                pb: { xs: 'calc(env(safe-area-inset-bottom, 16px) + 12px)', sm: 2 },
              },
            }}
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {t('installInstructionsTitle')}
              </Typography>
              <IconButton size="small" onClick={() => setInfoOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 0.5 }}>
                {t('installInstructionsDesc')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('mobileTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2, px: 1, lineHeight: 1.6 }}>
                • {t('mobileStep1')}<br />
                • {t('mobileStep2')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t('desktopTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 1, lineHeight: 1.6 }}>
                • {t('desktopStep1')}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button variant="contained" fullWidth onClick={() => setInfoOpen(false)} sx={{ borderRadius: 2 }}>
                {t('ok')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* About App Modal */}
          <AboutModal
            open={aboutOpen}
            onClose={() => setAboutOpen(false)}
            t={t}
            language={language}
          />
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;